import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { useCompoundEntityDetail, useCompoundEntitySourceRecords } from "~/features/compoundEntities/hooks";
import { toCompoundDetailUI, toCompoundSourceRecordUIList } from "~/features/compoundEntities/utils/transformers";
import { CompoundEntityDetailPresenter } from "./CompoundEntityDetailPresenter";

interface CompoundEntityDetailProps {
  id: string;
  onBack: () => void;
  variant?: "page" | "peek";
}

export function CompoundEntityDetail({ id, onBack, variant = "page" }: CompoundEntityDetailProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { data } = useCompoundEntityDetail(id);

  const raw = data.compoundCompoundRecords[0];
  const sourceRecordIds = raw?.recordType === "entity" ? raw.recordIdArray : [];
  const {
    data: sourceRecordsData,
    visibleRecordIds,
    hasMore: hasMoreSourceRecords,
    loadMore: loadMoreSourceRecords,
    loading: sourceRecordsLoading,
    error: sourceRecordsError,
    retry: retrySourceRecords,
  } = useCompoundEntitySourceRecords(sourceRecordIds);
  const detail = useMemo(() => (raw ? toCompoundDetailUI(raw, locale) : null), [raw, locale]);
  const sourceRecords = useMemo(
    () => (sourceRecordsData ? toCompoundSourceRecordUIList(sourceRecordsData.records, visibleRecordIds, locale) : []),
    [locale, sourceRecordsData?.records, visibleRecordIds],
  );

  if (!raw || !detail) {
    return <div className="mx-auto max-w-[var(--content-max)] p-8 text-[13px] text-fg-3">{t("ui.notFound")}</div>;
  }

  return (
    <CompoundEntityDetailPresenter
      detail={detail}
      sourceRecords={sourceRecords}
      hasMoreSourceRecords={hasMoreSourceRecords}
      onLoadMoreSourceRecords={loadMoreSourceRecords}
      sourceRecordsLoading={sourceRecordsLoading}
      sourceRecordsLoadFailed={Boolean(sourceRecordsError)}
      onRetrySourceRecords={retrySourceRecords}
      onBack={onBack}
      variant={variant}
    />
  );
}
